import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import LandingRoute from '../views/LandingRoute';
import QuickResearchRoute from '../views/QuickResearchRoute';
import LookupRoute from '../views/LookupRoute';
import SettingsRoute from '../views/SettingsRoute';
import BillingRoute from '../views/BillingRoute';
import ConnectRoute from '../views/ConnectRoute';
import AdminRoute from '../views/AdminRoute';
import InventoryRoute from '../views/InventoryRoute';
import InstitutionRoute from '../views/InstitutionRoute';
import NewProjectRoute from '../views/NewProjectRoute';
import ProjectsRoute from '../views/ProjectsRoute';
import { useAuthActions } from '../context/AuthContext';
import ReportingPreviewRoute from '../views/ReportingPreviewRoute';
import ClassificationRoute from '../views/ClassificationRoute';
import { useShellUiActions } from '../context/ShellUiContext';
import { isChatLocked, type CustomerModuleId } from '../utils/moduleAccess';

// BAR-763: every customer-facing route represented by the five-module
// entitlement registry uses the same fail-closed navigation behavior. A denied
// deep link never renders the restricted view: it returns Home and opens the
// existing explanatory module dialog.
function LockedRouteGate({
  locked,
  label,
  children,
}: {
  locked: boolean;
  label: string;
  children: React.ReactNode;
}) {
  const { onOpenUnlockModule } = useShellUiActions();

  React.useEffect(() => {
    if (locked) onOpenUnlockModule(label);
  }, [locked, label, onOpenUnlockModule]);

  if (locked) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ModuleGate({
  moduleId,
  label,
  children,
}: {
  moduleId: CustomerModuleId;
  label: string;
  children: React.ReactNode;
}) {
  const { isModuleDisabled } = useAuthActions();
  return (
    <LockedRouteGate locked={isModuleDisabled(moduleId)} label={label}>
      {children}
    </LockedRouteGate>
  );
}

// BAR-1528: Chat is in none of the five modules. The orchestrator serves
// it to any organization with web access and refuses it (403
// `web_access_not_in_plan`) only when every web module is off, so the route
// gates on exactly that. Gating it on one module would take Chat away from
// organizations the server still serves.
function WebAccessGate({ label, children }: { label: string; children: React.ReactNode }) {
  const { isModuleDisabled, usesChatFree } = useAuthActions();
  return (
    <LockedRouteGate locked={isChatLocked(isModuleDisabled, usesChatFree)} label={label}>
      {children}
    </LockedRouteGate>
  );
}

// `/projects/new` serves both project types. Match routes.ts's default:
// missing/unknown `type` means Research; `type=build` selects Build.
function NewProjectGate({ children }: { children: React.ReactNode }) {
  const { search } = useLocation();
  const projectType = new URLSearchParams(search).get('type');
  const isBuild = projectType === 'build';

  return (
    <ModuleGate
      moduleId={isBuild ? 'build_artifact' : 'regulatory_research'}
      label={isBuild ? 'Build' : 'Regulatory research'}
    >
      {children}
    </ModuleGate>
  );
}

// BAR-40: the SPA's view route table — the single source of truth for which
// URL renders which view. Rendered as a descendant <Routes> inside the
// persistent shell (MainApplicationUI), so the chrome never remounts on
// navigation. Paths MUST match routes.ts's navStateToPath/pathToNavState
// exactly (landing = '/', new-project = '/projects/new', etc.) — this is what
// the routing tests assert.
//
// The project-scoped views (`workspaceView` = the research workspace canvas,
// shared by /research/:id and its /logic-chain alias which render identically,
// plus the no-project-yet draft at /research/new; `buildArtifactsView` = the
// build-artifacts canvas, also covering the no-project-yet draft at
// /build/new). These render the workspace/build canvas
// VERBATIM from the persistent shell (MainApplicationUI) — the canvas is too
// coupled to the shell's local refs/state (linkedReport, pendingDraft, the
// report-panel split refs) to relocate without an authed e2e to verify it. The
// win that matters for BAR-40 is here: this route table — not a `mainView`
// ternary — is what selects between workspace and build. Deep-link hydration
// stays in the shell's existing effect. Unknown paths redirect to landing
// (preserving the pre-extraction "unrecognized view → home" behavior).
// BAR-124 prod gate (PR #531): the inventory route exists only where its API
// is explicitly configured (REACT_APP_INVENTORY_API) or in local dev. Prod
// builds set neither, so no route is registered and /inventory falls through
// to the catch-all redirect. Note: this is a RUNTIME gate — the panel code
// still ships in the bundle (CRA cannot statically fold the env lookup); what
// prod users can never do is reach it. The gate opens per environment by
// setting REACT_APP_INVENTORY_API at build time.
// Ship split (Kawi, 21 Jul: "i want to ship the institution profile, not
// the inventory"): the API base alone opens ONLY /institution. The
// inventory route additionally needs REACT_APP_INVENTORY_UI=true — so a
// dev/prod build can carry the profile while the inventory stays absent.
// Local dev keeps both.
export const INSTITUTION_ENABLED =
  Boolean(process.env.REACT_APP_INVENTORY_API) || process.env.NODE_ENV === 'development';
// exported for the sidebar door (Kawi, 5 Aug: "add the gated menu entry if
// it doesnt impact the security") — the MENU shows exactly where the ROUTE
// exists; where this is false there is no door and no page, so the entry
// adds no exposure. The real gate stays the build flag, never the menu.
export const INVENTORY_ENABLED =
  (Boolean(process.env.REACT_APP_INVENTORY_API) && process.env.REACT_APP_INVENTORY_UI === 'true')
  || process.env.NODE_ENV === 'development';

export default function ShellRoutes({
  workspaceView,
  buildArtifactsView,
  classificationOpenVersion,
}: {
  workspaceView: React.ReactNode;
  buildArtifactsView: React.ReactNode;
  classificationOpenVersion?: number;
}) {
  const workspace = <>{workspaceView}</>;
  const build = <>{buildArtifactsView}</>;
  return (
    <Routes>
      <Route index element={<LandingRoute />} />
      <Route
        path="lookup/:chatId?"
        element={
          <WebAccessGate label="Chat">
            <LookupRoute />
          </WebAccessGate>
        }
      />
      <Route
        path="quick-research"
        element={
          <ModuleGate moduleId="quick_lookup" label="Quick lookup">
            <QuickResearchRoute />
          </ModuleGate>
        }
      />
      <Route path="settings" element={<SettingsRoute />} />
      {/* The route verifies the independent server entitlement before mounting any UI. */}
      <Route path="classification/:projectId?" element={<ClassificationRoute openVersion={classificationOpenVersion} />} />
      {/* BAR-1294: NOT module-gated, on purpose. A paused customer's module map
          is all false, and this is the page that lets them pay and get it back.
          Gating it would strand exactly the person it exists for. */}
      <Route path="billing" element={<BillingRoute />} />
      {/* NOT module-gated either. A free (MCP-only) account has every web
          module off, and this page, which connects its AI assistant, is where
          it lands (LandingRoute sends it here from `/`). */}
      <Route path="connect" element={<ConnectRoute />} />
      <Route path="admin" element={<AdminRoute />} />
      {/* The `reporting` module (admin panel, default OFF). The auth service
          checks the same module before it serves the document. */}
      <Route
        path="reporting/preview"
        element={
          <ModuleGate moduleId="reporting" label="Reporting">
            <ReportingPreviewRoute />
          </ModuleGate>
        }
      />
      {INVENTORY_ENABLED && <Route path="inventory" element={<InventoryRoute />} />}
      {/* BAR-124, 20 Jul: the institution profile as its own page (master
          data — Research/Inventory/reporting all read it). Since 21 Jul it
          has its own gate: the API base opens it, the inventory needs the
          extra UI flag above. */}
      {INSTITUTION_ENABLED && <Route path="institution" element={<InstitutionRoute />} />}
      <Route
        path="projects/new"
        element={<NewProjectGate><NewProjectRoute /></NewProjectGate>}
      />
      <Route
        path="projects"
        element={
          <ModuleGate moduleId="project_library" label="Project library">
            <ProjectsRoute />
          </ModuleGate>
        }
      />
      <Route
        path="research/new"
        element={
          <ModuleGate moduleId="regulatory_research" label="Regulatory research">
            {workspace}
          </ModuleGate>
        }
      />
      <Route
        path="research/:projectId"
        element={
          <ModuleGate moduleId="regulatory_research" label="Regulatory research">
            {workspace}
          </ModuleGate>
        }
      />
      <Route
        path="research/:projectId/logic-chain"
        element={
          <ModuleGate moduleId="regulatory_research" label="Regulatory research">
            {workspace}
          </ModuleGate>
        }
      />
      <Route
        path="build/new"
        element={
          <ModuleGate moduleId="build_artifact" label="Build">
            {build}
          </ModuleGate>
        }
      />
      <Route
        path="build/:projectId"
        element={
          <ModuleGate moduleId="build_artifact" label="Build">
            {build}
          </ModuleGate>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
